// use-hls-media.ts
import * as Sentry from "@sentry/nextjs";
import type { ErrorData } from "hls.js";
import Hls from "hls.js";
import last from "lodash/last";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { preferNativeHls } from "ui/lib/hls-support";

interface UseHlsMediaProps {
  autoPlay?: boolean;
  url: string;
  ref: React.RefObject<HTMLVideoElement>;
  /**
   * Skip the native-HLS Safari shortcut and always use hls.js. Use when
   * you need programmatic control over the initial rendition (e.g., short-
   * form playback where Safari's native ABR starts too low and its cached
   * low-rez segments replay on scrub-back).
   */
  forceHls?: boolean;
}

interface UseHlsMediaReturn {
  error: ErrorData | null;
  isSupported: boolean;
  isHlsMedia: boolean;
  isNativeSupported: boolean;
  cleanUpHls: () => void;
}

const MAX_MEDIA_RECOVERIES = 3;
// A loop recovers every few seconds; errors further apart than this are
// unrelated (e.g. a long audio session) and each gets a fresh budget.
const MEDIA_RECOVERY_WINDOW_MS = 30_000;

export const checkIsHlsMedia = (url: string): boolean => {
  try {
    if (!url || typeof url !== "string") return false;
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const extension = last(pathname.split("."));
    return extension === "m3u8";
  } catch (error) {
    console.error({ url, error });
    Sentry.captureException(error, {
      tags: { function: "checkIsHlsMedia" },
      extra: { url },
    });
    return false;
  }
};

/**
 * Plays HLS streams. Prefers native HLS when the browser supports it
 * (Safari / iOS — Apple's and Mux's recommended path) and falls back
 * to hls.js (Chrome, Firefox, Android) otherwise.
 *
 * The previous implementation forced hls.js on Safari, which could
 * hit MSE codec incompatibilities that native playback wouldn't, and
 * its error handler closed over a stale `retryAttempts` value that
 * never actually incremented in the captured closure — so NETWORK_ERROR
 * events retried immediately, in a tight loop, while React re-rendered
 * on every setRetryAttempts call.
 */
export const useHlsMedia = ({
  autoPlay = false,
  url,
  ref: playerRef,
  forceHls = false,
}: UseHlsMediaProps): UseHlsMediaReturn => {
  const [isSupported, setIsSupported] = useState<boolean>(true);
  const [isHlsMedia, setIsHlsMedia] = useState<boolean>(checkIsHlsMedia(url));
  const [isNativeSupported, setIsNativeSupported] = useState<boolean>(false);
  const [error, setError] = useState<ErrorData | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  // Ref, not state. The hls.js error handler closure needs to read the
  // current attempt count; state would be captured once per attach.
  const retryAttemptsRef = useRef<number>(0);
  const mediaRecoveriesRef = useRef<number>(0);
  const lastMediaRecoveryAtRef = useRef<number>(0);

  const cleanUpHls = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  useEffect(() => {
    setIsHlsMedia(checkIsHlsMedia(url));

    // Check for native HLS support.
    const probe = document.createElement("video");
    const canPlay =
      probe.canPlayType("application/vnd.apple.mpegurl") === "probably" ||
      probe.canPlayType("application/vnd.apple.mpegurl") === "maybe";
    setIsNativeSupported(canPlay);
  }, [url]);

  useEffect(() => {
    // Recompute rather than read the `isHlsMedia` state: that state is set
    // by the effect above on the same `url` change, so this effect can
    // still observe last render's stale value. On a component that stays
    // mounted while `url` flips from HLS to falsy (e.g. playlist advance
    // to a missing clip), the stale true + new empty url combo used to
    // fire a spurious "URL is not provided" report.
    if (!checkIsHlsMedia(url)) return;

    if (!playerRef.current) {
      console.error("Player reference is not available");
      Sentry.captureMessage("Player reference is not available", {
        level: "warning",
        extra: { url },
      });
      return;
    }

    const v = playerRef.current;

    // Reset retry counter on every attach so a successful re-attach
    // (e.g., after url change) starts fresh.
    retryAttemptsRef.current = 0;
    mediaRecoveriesRef.current = 0;

    // Prefer native HLS on WebKit. Avoids hls.js MSE codec issues that
    // bite Safari on certain Mux renditions. Chromium browsers also claim
    // native HLS support since ~150, but their pipeline is unreliable —
    // preferNativeHls routes them to hls.js (LADD-4313).
    if (!forceHls && preferNativeHls(v)) {
      v.src = url;
      if (autoPlay) {
        v.play().catch((playError) => {
          // Autoplay refusal is a user-environment outcome (iOS Low Power
          // Mode, Safari autoplay policy); AbortError fires when a new
          // load() supersedes the in-flight play(). Neither is a bug.
          const name = (playError as Error | undefined)?.name;
          if (name === "NotAllowedError" || name === "AbortError") return;
          Sentry.captureException(playError, {
            tags: { function: "useHlsMedia.nativeAutoPlay" },
            extra: { url },
          });
        });
      }
      return () => {
        v.removeAttribute("src");
        v.load();
      };
    }

    if (!Hls.isSupported()) {
      setIsSupported(false);
      return;
    }

    const hls = new Hls({
      // Start the ABR ladder from a realistic bandwidth assumption rather
      // than hls.js's 500kbps default, which picks the lowest rendition for
      // the first segment and causes a visible "first ~10s looks soft, then
      // snaps to HD" ramp on short-form video (intros/outros, hero loops).
      // Once a low-rendition segment is fetched it stays in the fragment
      // buffer, so scrubbing back to the start still looks soft — the fix
      // has to happen at initial-level selection, not after.
      abrEwmaDefaultEstimate: 5_000_000,
      // Don't intentionally start low to probe bandwidth — same reason.
      testBandwidth: false,
    });
    hlsRef.current = hls;
    hls.attachMedia(v);

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(url);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      setError(null);
      // Pin to the top rendition from the very first fragment. Short-form
      // content (intros/outros, hero loops, chat attachments) doesn't
      // benefit from adaptive ramp-up — the ramp is visible as a ~10s
      // "soft then snaps to HD" lead-in, and previously-buffered low
      // fragments stay in buffer so scrubbing back looks soft too.
      // Setting currentLevel after MANIFEST_PARSED forces the first load
      // at max quality. ABR stays disabled while currentLevel is fixed.
      const top = hls.levels.length - 1;
      if (top > 0) hls.currentLevel = top;
      if (autoPlay) {
        v.play().catch((playError) => {
          const name = (playError as Error | undefined)?.name;
          if (name === "NotAllowedError" || name === "AbortError") return;
          Sentry.captureException(playError, {
            tags: { function: "useHlsMedia.hlsAutoPlay" },
            extra: { url },
          });
        });
      }
    });

    hls.on(Hls.Events.ERROR, (event, data: ErrorData) => {
      // hls.js auto-recovers from non-fatal errors (buffer stalls,
      // transient fragment issues). Reporting every tick fans out
      // into high-volume noise that masks real failures — see
      // LADDER-WEBSITE-89H (bufferStalledError, fatal:false, 167e/12h)
      // and APOLLO-204 (same sub-error, 3034e lifetime).
      if (!data.fatal) return;

      Sentry.captureMessage(`HLS Error: ${data.type}`, {
        level: "error",
        tags: { hlsEvent: data.type },
        extra: { event, data, url },
      });

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR: {
          // Read from the ref so the check and delay use the actual
          // current attempt count, not the initial closure value.
          if (retryAttemptsRef.current < 25) {
            const nextAttempt = retryAttemptsRef.current + 1;
            retryAttemptsRef.current = nextAttempt;
            setTimeout(
              () => hlsRef.current?.recoverMediaError(),
              nextAttempt * 500
            );
          } else {
            Sentry.captureMessage(
              "Max retry attempts reached for NETWORK_ERROR",
              { level: "warning", extra: { url } }
            );
            setError(data);
          }
          break;
        }
        case Hls.ErrorTypes.MEDIA_ERROR: {
          // recoverMediaError re-attaches the element, which re-runs the
          // MEDIA_ATTACHED -> loadSource handler above. An error that comes
          // back on every load (the browser can't decode any variant) would
          // otherwise loop forever, filing one event per cycle.
          const now = Date.now();
          if (now - lastMediaRecoveryAtRef.current > MEDIA_RECOVERY_WINDOW_MS) {
            mediaRecoveriesRef.current = 0;
          }
          if (
            data.details === Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR ||
            mediaRecoveriesRef.current >= MAX_MEDIA_RECOVERIES
          ) {
            setError(data);
            cleanUpHls();
            break;
          }
          mediaRecoveriesRef.current += 1;
          lastMediaRecoveryAtRef.current = now;
          try {
            hlsRef.current?.recoverMediaError();
          } catch (recoverError) {
            Sentry.captureException(recoverError, {
              tags: { hlsEvent: "MEDIA_ERROR_RECOVERY_FAILED" },
              extra: { url },
            });
            setError(data);
            cleanUpHls();
          }
          break;
        }
        default:
          setError(data);
          Sentry.captureMessage(`Unrecoverable HLS error: ${data.type}`, {
            level: "error",
            extra: { url },
          });
          cleanUpHls();
          break;
      }
    });

    return () => {
      cleanUpHls();
    };
  }, [url, autoPlay, forceHls]);

  return {
    error,
    isSupported,
    isHlsMedia,
    isNativeSupported,
    cleanUpHls,
  };
};
